import { debounce } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DataGrid,
  GridFilterModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import { ServiceDataGridProps } from '@/components/ServiceDataGrid/ServiceDataGrid.types';
import { useServiceDataGrid } from '@/components/useServiceDataGrid/useServiceDataGrid';
import MuiFilterToolbar from '@/components/ServiceDataGrid/MultiFilterBuilder/MuiFilterToolbar';

/**
 * The main data grid component that handles:
 * - Server-side pagination
 * - Server-side sorting
 * - Server-side filtering (including quick search and advanced filter)
 */
const ServiceDataGrid = React.forwardRef(function ServiceDataGrid<
  T extends GridValidRowModel,
>(
  {
    service,
    columns,
    paginationMode = 'server',
    sortingMode = 'server',
    pageSize = 10,
    sortModel: initialSortModel,
    filterModel: initialFilterModel,
    messageQueue,
    onQueryUpdated,
    rowFilter,
    showMultiFilterBuilder = true,
    quickSearchFields,
    rowId,
    toolbarCustomComponent,
    ...props
  }: ServiceDataGridProps<T> & { showMultiFilterBuilder?: boolean },
  // The ref is unused here, but we accept it so the forwardRef is valid
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: React.ForwardedRef<Element>
) {
  const apiRef = useGridApiRef();

  /**
   * Quick search input value (client-side).
   * We'll debounce changes before sending them to the server.
   */
  const [quickSearchValue, setQuickSearchValue] = useState('');

  /**
   * Debounced setter for quick search to avoid rapid server calls.
   */
  const debouncedHandleQuickSearchChange = useMemo(
    () =>
      debounce((newValue: string) => {
        setQuickSearchValue(newValue);
      }, 500),
    []
  );

  /**
   * Use our custom server-side hook for data fetching, pagination, and sorting.
   * We pass it `localFilterModel` (not the original prop) so the hook
   * reflects local changes from the user.
   */
  const {
    rows,
    handleFilterModelChange,
    filterModel,
    loading,
    rowCount,
    paginationMeta,
    paginationModel,
    handlePaginationModelChange,
  } = useServiceDataGrid<T>({
    apiRef,
    service,
    messageQueue,
    pageSize,
    onQueryUpdated,
    initialSortModel,
    initialFilterModel,
    rowFilter,
    quickSearchValue,
    quickSearchFields: quickSearchFields || [],
  });

  // Keep track of the filterModel in a ref for the MultiFilterBuilder
  const filterModelRef = useRef<GridFilterModel>(filterModel);

  /**
   * Sync changes from the outside:
   */
  useEffect(() => {
    filterModelRef.current = filterModel;
  }, [filterModel]);

  /**
   * Disable column-level filtering inside the grid to avoid confusion with the
   * custom toolbar filters. We keep the original columns for the toolbar's
   * field list, but pass non-filterable columns to the DataGrid.
   */
  const nonFilterableColumns = useMemo(
    () => columns.map(col => ({ ...col, filterable: false })),
    [columns]
  );

  /**
   * Callback for when the user finishes editing filters in the MultiFilterBuilder.
   * Pass the new model to the parent component.
   */
  const handleFilterModelBuilderChange = useCallback(
    (newModel: GridFilterModel) => {
      // The api isn't used here to avoid the single filter restriction on the DataGrid
      handleFilterModelChange(newModel);
    },
    [handleFilterModelChange]
  );

  /**
   * We'll provide a custom toolbar if requested,
   * which shows advanced filters + quick search.
   */
  const CustomToolbar = useCallback(() => {
    return (
      <MuiFilterToolbar
        columns={columns.map(col => ({
          field: col.field,
          headerName: col.headerName ?? col.field,
        }))}
        filterModelRef={filterModelRef}
        onFilterModelChange={handleFilterModelBuilderChange}
        onQuickSearchChange={debouncedHandleQuickSearchChange}
        customComponent={toolbarCustomComponent}
      />
    );
  }, [
    columns,
    handleFilterModelBuilderChange,
    debouncedHandleQuickSearchChange,
    toolbarCustomComponent,
  ]);

  return (
    <div style={{ width: '100%' }}>
      <DataGrid
        {...props}
        apiRef={apiRef}
        getRowId={row => (rowId ? row[rowId] : row.id)}
        rows={rows}
        columns={nonFilterableColumns}
        initialState={{
          ...props.initialState,
          pagination: {
            ...props.initialState?.pagination,
            paginationModel: {
              ...props.initialState?.pagination?.paginationModel,
              page: 0,
              pageSize,
            },
          },
        }}
        paginationModel={{ page: paginationModel?.page ?? 0, pageSize }}
        onPaginationModelChange={model =>
          handlePaginationModelChange({ page: model.page, pageSize })
        }
        pageSizeOptions={[pageSize]}
        paginationMode={paginationMode}
        paginationMeta={paginationMeta}
        sortingMode={sortingMode}
        loading={loading}
        rowCount={rowCount}
        slots={{
          toolbar: showMultiFilterBuilder ? CustomToolbar : undefined,
        }}
        disableColumnFilter
      />
    </div>
  );
});

export { ServiceDataGrid };
